<template>
  <span>
    <GmvMap
      ref="gmap"
      map-key="mainMap"
      :center="center"
      :zoom="zoom"
      map-type-id="terrain"
      style="height: 100%; width: 100%"
      :options="options"
      @center_changed="saveMapLocation($event)"
      @zoom_changed="updateZoom($event)"
      @click="closeInfoWindow"
    >
      <MapPin
        v-for="(coords, deviceId, index) in devices"
        :key="deviceId"
        :deviceId="deviceId"
        :clickable="clickable"
        :ref="setPinRef(deviceId)"
        :moving="setPinIsMoving(deviceId)"
        :zIndex="setPinZIndex(deviceId, index)"
        :opacity="setPinOpacity(deviceId)"
        data-cy="mapPin"
        @clicked="toggleInfoWindow($event)"
        @closed="closePin($event)"
      />
      <GmvInfoWindow
        v-if="infoWinOpen && currentPinId !== null"
        :key="currentPinId"
        :options="infoOptions"
        :markerKey="`marker-${currentPinId}`"
        :infoWindowKey="`infoWindow-${currentPinId}`"
        :opened="true"
        @closeclick="closeInfoWindow"
      >
        <DevicePopover
          :deviceId="currentPinId"
          @close="closeInfoWindow"
          @openHistoryDialog="openHistoryDialog"
        />
      </GmvInfoWindow>
      <MapSettingsDialog :open="dialogs.mapSettings" @close="dialogs.mapSettings = false" />
    </GmvMap>
    <v-btn
      class="map-settings-button"
      color="white"
      @click="toggleMapSettings()"
      data-cy="mapSettingsButton"
      min-width="40"
      height="40"
      width="40"
    >
      <v-icon size="x-large">mdi-cog</v-icon>
    </v-btn>
    <HistoryDialog
      :open="historyOpen"
      :currentDeviceId="historyCurrentDeviceId"
      :isDetachedDialog="true"
      :removableDevices="true"
      :scrim="false"
      @close="closeHistoryDialog"
    />
  </span>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useLocationGetter } from '@/services/location'
import { useStreaming } from '@/composables/useStreaming'
import MapSettingsDialog from '@/components/dialogs/MapSettings.vue'
import MapPin from '@/components/MapPin.vue'
import DevicePopover from '@/components/devicePopover/layouts/devicePopover.vue'
import HistoryDialog from '@/components/dialogs/HistoryDialog.vue'
import { deviceModels } from '@/store/subscriptions/utils'
import { DeviceType } from '@/api/services/utils'
import { NAMESPACES } from '@/store/types'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import { MAP_ACTIONS, MAP_GETTERS } from '@/store/map'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import type { LocationCoords } from '@/store/location'

import throttle from 'lodash/throttle'
import { utilities } from '@gmap-vue/v3'
import { THROTTLE_INTERVAL } from '@/services/device-metadata'
import type { DeviceToMove, DeviceToView } from '@/store/map/types'
import type { DeviceCoords } from '@/services/device-service/devices'
import type { GmvMapRef } from '@/types/map'

const { getGoogleMapsAPI } = utilities

interface MarkerEvent {
  coords: DeviceCoords
  deviceId: string
  deviceType: number
}

export default defineComponent({
  name: 'VMap',
  components: {
    MapSettingsDialog,
    MapPin,
    DevicePopover,
    HistoryDialog
  },
  setup() {
    const { getLocation, setLocation } = useLocationGetter()
    const { enableTelemetryStreaming, openTelemetryStream, closeTelemetryStream } = useStreaming()
    return {
      getLocation,
      setLocation,
      enableTelemetryStreaming,
      openTelemetryStream,
      closeTelemetryStream
    }
  },
  data() {
    return {
      center: { lat: 30, lng: -87 } as DeviceCoords,
      infoWinOpen: false,
      currentPinId: null as string | null,
      lastPinId: null as string | null,
      topZIndex: Object.keys(this.$store.state[NAMESPACES.DEVICES]?.coords ?? {}).length,
      pinsHeightMap: {} as Record<string, number>,
      infoOptions: {
        //optional: offset infowindow so it visually sits nicely on top of our marker
        pixelOffset: {
          width: -1,
          height: 6
        }
      },
      dialogs: {
        mapSettings: false
      },
      historyOpen: false,
      historyCurrentDeviceId: null as string | null,
      throttledGetDeviceData: null as ReturnType<typeof throttle> | null
    }
  },
  computed: {
    options(): Record<string, unknown> {
      return {
        mapId: APP_ENV.VITE_APP_GMAP_MAP_ID,
        clickableIcons: false,
        streetViewControl: false,
        zoomControl: false,
        mapTypeControl: true,
        fullscreenControl: false,
        minZoom: 4,
        maxZoom: 24,
        restriction: {
          latLngBounds: {
            north: 85,
            south: -85,
            west: -180,
            east: 180
          }
        }
      }
    },
    google: getGoogleMapsAPI,
    zoom(): number {
      return this.$store.state.map.location.zoom
    },
    inMovingMode(): boolean {
      return this.$store.state.map.movingMode
    },
    movingPin(): string | undefined {
      return (this.$store.state.map.deviceToMove as DeviceToMove | null)?.deviceId
    },
    clickable(): boolean {
      return !this.inMovingMode
    },
    devices(): Record<string, DeviceCoords> {
      return this.$store.state.devices.coords
    },
    allDeviceModels(): string[] {
      return this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODELS}`]
    }
  },
  watch: {
    inMovingMode(isMoving: boolean) {
      if (isMoving) {
        this.setMapToMovingMode()
      }
    },
    allDeviceModels(newVal: string[], oldVal: string[]) {
      if (oldVal.length === 0 && newVal.length > 0) {
        this.ensureDeviceModelsPopulated()
      }
    }
  },
  async mounted() {
    if (this.$store.state.map.deviceViewMode) {
      const { deviceId, deviceType } = this.$store.state.map.deviceToView as DeviceToView
      const coords: DeviceCoords =
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`](
          deviceId
        )
      this.center = coords
      this.setLocation(coords)
      this.toggleInfoWindow({ coords, deviceId, deviceType })
    } else {
      this.getLocation((coords: LocationCoords) => {
        if (coords) {
          this.center = this.inMovingMode
            ? this.$store.getters[
                `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`
              ]((this.$store.state.map.deviceToMove as DeviceToMove | null)?.deviceId)
            : coords
        }
      })
    }

    if (this.inMovingMode) {
      this.setMapToMovingMode()
    }

    await this.setControls()
    this.ensureDeviceModelsPopulated()
  },
  unmounted() {
    if (this.$store.state.map.deviceViewMode) {
      this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.STOP_DEVICE_VIEW_MODE}`)
    }
  },
  methods: {
    updateZoom(event: number): void {
      this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.SET_ZOOM}`, event)
    },
    toggleMapSettings(): void {
      this.dialogs.mapSettings = !this.dialogs.mapSettings
    },
    async openHistoryDialog(deviceId: string): Promise<void> {
      this.historyCurrentDeviceId = deviceId
      this.historyOpen = true
      await this.$nextTick()
      this.historyCurrentDeviceId = null
    },
    closeHistoryDialog(): void {
      this.historyOpen = false
      this.historyCurrentDeviceId = null
    },
    ensureDeviceModelsPopulated(): void {
      const selectedModels: string[] | null =
        this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_DEVICE_MODELS}`]
      if (!selectedModels || selectedModels.length <= 0) {
        this.$store.dispatch(`${NAMESPACES.MAP}/${MAP_ACTIONS.UPDATE_SETTINGS}`, {
          deviceModels: this.allDeviceModels.filter(
            (model: string) => model !== deviceModels.SMART_BRIDGE
          )
        })
      }
    },
    closePin(event: string): void {
      if (event === this.currentPinId) {
        this.closeInfoWindow()
      }
    },
    closeDeviceSettings(marker: MarkerEvent): void {
      this.closeInfoWindow()
      this.toggleInfoWindow(marker)
    },
    toggleInfoWindow(marker: MarkerEvent): void {
      // check if its the same marker that was selected if yes toggle
      if (this.currentPinId === marker.deviceId) {
        this.infoWinOpen = !this.infoWinOpen
      } else {
        // if different marker set infowindow to open and reset current marker index
        this.infoWinOpen = true
        this.lastPinId = this.currentPinId
        this.currentPinId = marker.deviceId
      }

      // refresh device data if infowindow is open
      if (this.infoWinOpen) {
        this.deviceDataHandler(marker)
      }
    },
    closeInfoWindow(): void {
      if (this.infoWinOpen) {
        this.lastPinId = this.currentPinId
        this.currentPinId = null
        this.infoWinOpen = false
        this.closeTelemetryStream()
        this.throttledGetDeviceData?.cancel()
      }
    },
    async setControls(): Promise<void> {
      await this.$gmapApiPromiseLazy()
      const map = await (this.$refs.gmap as GmvMapRef)?.mapPromise
      map?.setOptions({
        ...this.options,
        cameraControlOptions: {
          position: this.google?.maps?.ControlPosition?.RIGHT_TOP
        }
      })
    },
    async setMapToMovingMode(): Promise<void> {
      await this.$gmapApiPromiseLazy()
      const device = this.$store.state.map.deviceToMove as DeviceToMove | null
      if (device) {
        this.dialogs.mapSettings = false
        this.infoWinOpen = false
        const map = await (this.$refs.gmap as GmvMapRef)?.mapPromise
        map?.panTo(device.coords)
      }
    },
    saveMapLocation(event: { lat: () => number; lng: () => number }): void {
      const coords: DeviceCoords = {
        lat: event.lat(),
        lng: event.lng()
      }
      this.setLocation(coords)
    },
    async deviceDataHandler(device: MarkerEvent): Promise<void> {
      if (this.enableTelemetryStreaming) {
        this.throttledGetDeviceData = throttle(() => this.getDeviceData(device), THROTTLE_INTERVAL)
        await this.openTelemetryStream(device.deviceId, this.throttledGetDeviceData as () => void)
      } else {
        this.$store.dispatch(
          `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_LATEST_TELEMETRY_AND_UPDATE}`,
          device.deviceId
        )
        this.getDeviceData(device)
      }
    },
    getDeviceData(device: MarkerEvent): void {
      const { deviceId, deviceType } = device
      this.$store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_TELEMETRY_AVERAGES_AND_UPDATE}`,
        deviceId
      )
      if ([DeviceType.USER, DeviceType.SHARED].includes(deviceType)) {
        this.$store.dispatch(
          `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_DEVICE_DETAILS_AND_UPDATE}`,
          { deviceId }
        )
      }
      this.$store.dispatch(
        `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.GET_PERCENT_CHANGED_AND_UPDATE}`,
        deviceId
      )
    },
    setPinRef(deviceId: string): string {
      if (this.inMovingMode && deviceId === this.movingPin) {
        return 'movingPin'
      }
      return deviceId
    },
    setPinIsMoving(deviceId: string): boolean {
      return this.inMovingMode && deviceId === this.movingPin
    },
    setPinZIndex(deviceId: string, index: number): number {
      if (this.inMovingMode && deviceId === this.movingPin) {
        return (this.google?.maps?.Marker?.MAX_ZINDEX ?? 0) + 1
      }

      if (deviceId === this.currentPinId) {
        return this.google?.maps?.Marker?.MAX_ZINDEX ?? 0
      }

      if (deviceId === this.lastPinId) {
        this.pinsHeightMap[deviceId] = ++this.topZIndex
        this.lastPinId = null
        return this.pinsHeightMap[deviceId]
      }

      return index
    },
    setPinOpacity(deviceId: string): number {
      if (this.inMovingMode) {
        return deviceId === this.movingPin ? 1 : 0.56
      }
      return 1
    }
  }
})
</script>
<style lang="scss">
.gm-style-iw-t {
  .gm-style-iw-c {
    padding: 0 !important;
    width: 396px !important;
    // Prevent popover width getting too narrow at phone resolutions
    @media (max-width: 900px) {
      max-width: 376px !important;
    }
    @media (max-width: 460px) {
      max-width: 326px !important;
    }
    @media (max-width: 400px) {
      max-width: 300px !important;
    }
    .gm-style-iw-d {
      overflow-x: hidden !important;
      width: 396px !important;
      @media (max-width: 900px) {
        max-width: 376px !important;
      }
      @media (max-width: 460px) {
        max-width: 326px !important;
      }
      @media (max-width: 400px) {
        max-width: 300px !important;
      }
    }
  }
  button.gm-ui-hover-effect {
    top: 0 !important;
    right: 0 !important;
    display: none !important;
  }
}
.gm-style-iw-t:after {
  background: #ffffff !important;
}
.gm-style .gm-style-iw-d::-webkit-scrollbar {
  width: 0 !important;
}

// hides the Google logo on bottom left of screen
a[href^='http://maps.google.com/maps'] {
  display: none !important;
}
a[href^='https://maps.google.com/maps'] {
  display: none !important;
}
.gm-style-iw-chr {
  display: none !important;
}
.map-settings-button {
  position: absolute;
  left: 10px;
  top: 160px;
  color: #666666 !important;

  &:hover {
    color: black !important;
  }
  &:hover:before {
    background-color: transparent;
  }
}

.map-zoom-button {
  position: absolute;
  left: 10px;
  top: 210px;
  color: #666666 !important;

  &:hover {
    color: black !important;
  }
  &:hover:before {
    background-color: transparent;
  }
}
</style>
